import { api } from '@/lib/api';

import { analysisErrorMessage, ANALYSIS_NOT_FOR_SCHOOL } from './studyAnalysisErrors';
import { studyAccessMessage } from './studyAccessMessages';

/**
 * Study Mind maps (study-mind-map-prd.md §3–§5, §8.2). The server owns maps, versions, limits,
 * prices and charges; the app edits the content JSON and shows the result. Every route is
 * learner-only: another learner's map is a 404.
 */

export const STUDY_MIND_MAP_COLORS = [
  'neutral',
  'coral',
  'bubblegum',
  'sunshine',
  'sky',
  'mint',
] as const;
export type StudyMindMapColor = (typeof STUDY_MIND_MAP_COLORS)[number];

export type StudyMindMapItemKind = 'key_point' | 'key_term' | 'formula' | 'question';

/**
 * Kinds of idea on the canvas (owner feedback 2026-10-09). Absent or `idea`: the plain idea card.
 * `group` is a resizable labelled frame behind other ideas and is the only kind with `w` / `h`.
 */
export const STUDY_MIND_MAP_NODE_KINDS = [
  'idea',
  'topic',
  'subtopic',
  'note',
  'question',
  'formula',
  'material',
  'group',
  'text',
] as const;
export type StudyMindMapNodeKind = (typeof STUDY_MIND_MAP_NODE_KINDS)[number];

/** Link styles (owner feedback 2026-10-09). The first value of each list is the default. */
export const STUDY_MIND_MAP_EDGE_COLORS = [
  'neutral',
  'ink',
  'slate',
  'coral',
  'bubblegum',
  'sunshine',
  'sky',
  'mint',
] as const;
export type StudyMindMapEdgeColor = (typeof STUDY_MIND_MAP_EDGE_COLORS)[number];
export const STUDY_MIND_MAP_EDGE_LINES = ['solid', 'dashed', 'dotted'] as const;
export type StudyMindMapEdgeLine = (typeof STUDY_MIND_MAP_EDGE_LINES)[number];
export const STUDY_MIND_MAP_EDGE_ARROWS = ['none', 'end', 'start', 'both'] as const;
export type StudyMindMapEdgeArrow = (typeof STUDY_MIND_MAP_EDGE_ARROWS)[number];
export const STUDY_MIND_MAP_EDGE_SHAPES = ['curve', 'elbow', 'straight'] as const;
export type StudyMindMapEdgeShape = (typeof STUDY_MIND_MAP_EDGE_SHAPES)[number];
export const STUDY_MIND_MAP_EDGE_WIDTHS = ['normal', 'thin', 'thick'] as const;
export type StudyMindMapEdgeWidth = (typeof STUDY_MIND_MAP_EDGE_WIDTHS)[number];

export type StudyMindMapSource =
  | {
      type: 'analysis_item';
      analysis_id: string;
      document_id: string;
      item_kind: StudyMindMapItemKind;
      item_id: string;
      page_number?: number | null;
    }
  | { type: 'document'; document_id: string; page_number?: number | null }
  | { type: 'deleted' }
  /** An idea the AI wrote without a material (study-mind-map-prd.md §5.5): "AI wrote this". */
  | { type: 'ai' };

export interface StudyMindMapNode {
  id: string;
  text: string;
  x: number;
  y: number;
  color?: StudyMindMapColor;
  /** "I'm not sure": the learner's own mark, never a score (PRD M5). */
  unsure?: boolean;
  note?: string;
  /** `ai`: from an AI draft ("From your page"). Absent: the learner's own idea. */
  origin?: 'ai' | 'learner';
  source?: StudyMindMapSource;
  /** Absent: a plain idea (owner feedback 2026-10-09). */
  kind?: StudyMindMapNodeKind;
  /** Frame size of a `group` (canvas points); other kinds never have them. */
  w?: number;
  h?: number;
}

/** Sides of an idea card where a link starts or ends. */
export type StudyMindMapSide = 'top' | 'right' | 'bottom' | 'left';
export const STUDY_MIND_MAP_SIDES: readonly StudyMindMapSide[] = ['top', 'right', 'bottom', 'left'];

export interface StudyMindMapEdge {
  id: string;
  from: string;
  to: string;
  label?: string;
  /**
   * The handles the learner joined (owner feedback 2026-10-09). Absent: the canvas picks the two
   * facing sides (`linkSides`). AI draft and migrated links never set them.
   */
  from_side?: StudyMindMapSide;
  to_side?: StudyMindMapSide;
  /** Link style (owner feedback 2026-10-09). Absent: the default (first value of each list). */
  color?: StudyMindMapEdgeColor;
  line?: StudyMindMapEdgeLine;
  arrow?: StudyMindMapEdgeArrow;
  shape?: StudyMindMapEdgeShape;
  width?: StudyMindMapEdgeWidth;
}

export interface StudyMindMapViewport {
  x: number;
  y: number;
  zoom: number;
}

export interface StudyMindMapContent {
  nodes: StudyMindMapNode[];
  edges: StudyMindMapEdge[];
  viewport: StudyMindMapViewport;
}

export const STUDY_MIND_MAP_LIMITS = {
  maps_per_subject: 50,
  nodes_per_map: 300,
  edges_per_map: 600,
  title_max: 80,
  node_text_max: 200,
  /** Node Details notes (owner feedback 2026-10-09); the server allows the same. */
  note_max: 5000,
  /** All note characters of one map together. */
  notes_total_max: 200_000,
  edge_label_max: 80,
  draft_analyses_max: 5,
  /** Group frame size bounds (canvas points); the server allows the same. */
  group_min_w: 160,
  group_min_h: 100,
  group_max_w: 4000,
  group_max_h: 4000,
} as const;

export const STUDY_MIND_MAP_DEFAULT_TITLE = 'Untitled map';
export const STUDY_MIND_MAP_LIMIT_MESSAGE =
  'You can have up to 50 mind maps in a subject. Delete one to make a new map.';
export const STUDY_MIND_MAP_CONFLICT_MESSAGE = 'This map changed on another device.';
export const STUDY_MIND_MAP_DELETED_SOURCE = 'This item was deleted';

export interface StudyMindMap {
  id: string;
  subject_key: string;
  term_id: string | null;
  title: string;
  version: number;
  node_count: number;
  edge_count: number;
  source_document_ids: string[];
  content: StudyMindMapContent;
  limits: { nodes_per_map: number; edges_per_map: number };
  created_at: string;
  updated_at: string;
}

export interface StudyMindMapPreview {
  nodes: Array<{ id: string; x: number; y: number; color?: StudyMindMapColor; text: string }>;
  /** Link colour and line style, so the list card draws dashed and coloured links. */
  edges: Array<{
    from: string;
    to: string;
    color?: StudyMindMapEdgeColor;
    line?: StudyMindMapEdgeLine;
  }>;
}

export interface StudyMindMapSummary {
  id: string;
  subject_key: string;
  term_id: string | null;
  title: string;
  version: number;
  node_count: number;
  edge_count: number;
  source_document_count: number;
  preview: StudyMindMapPreview;
  created_at: string;
  updated_at: string;
}

export interface StudyMindMapList {
  subject_key: string;
  items: StudyMindMapSummary[];
  limits: { maps_per_subject: number; nodes_per_map: number };
}

export type StudyMindMapProvenance =
  | 'learner_edit'
  | 'ai_draft'
  | 'ai_add'
  | 'restore'
  | 'migration';

export interface StudyMindMapRevisionSummary {
  version: number;
  title: string;
  provenance: StudyMindMapProvenance;
  node_count: number;
  created_at: string;
}

export interface StudyMindMapRevisions {
  map_id: string;
  current_version: number;
  items: StudyMindMapRevisionSummary[];
}

export interface StudyMindMapRevision {
  map_id: string;
  version: number;
  title: string;
  provenance: StudyMindMapProvenance;
  content: StudyMindMapContent;
  created_at: string;
}

/**
 * The four AI mind map actions (study-mind-map-prd.md §5.2, §5.5, §5.6). Each one is one paid
 * call: `materials` and `topic` make a new map, `add` adds to the open map ("Add from materials")
 * and `change` returns changes the learner previews and applies ("Ask AI").
 */
export type StudyMindMapDraftMode = 'materials' | 'topic' | 'add' | 'change';

/** Kinds the mind map skill may set on an idea (§5.4); `group` comes from `frame`. */
export const STUDY_MIND_MAP_AI_KINDS = [
  'topic',
  'subtopic',
  'idea',
  'note',
  'question',
  'formula',
  'text',
] as const;
export type StudyMindMapAiKind = (typeof STUDY_MIND_MAP_AI_KINDS)[number];

/** A link style from the skill: the edge style enums of §4.3 (a default value may be left out). */
export interface StudyMindMapAiLinkStyle {
  color?: StudyMindMapEdgeColor;
  line?: StudyMindMapEdgeLine;
  arrow?: StudyMindMapEdgeArrow;
  shape?: StudyMindMapEdgeShape;
  width?: StudyMindMapEdgeWidth;
}

/** Where an AI idea comes from: an analysis item, or `{ type: 'ai' }` ("AI wrote this"). */
export type StudyMindMapAiSource =
  | Extract<StudyMindMapSource, { type: 'analysis_item' }>
  | { type: 'ai' };

/** One idea of a skill tree (§5.4). `temp_id` is unique in one result. */
export interface StudyMindMapAiIdea {
  temp_id: string;
  text: string;
  kind?: StudyMindMapAiKind;
  note?: string;
  /** Words of a Group frame around every descendant of this idea. */
  frame?: string;
  source?: StudyMindMapAiSource;
  children?: Array<{
    link_label?: string;
    link_style?: StudyMindMapAiLinkStyle;
    idea: StudyMindMapAiIdea;
  }>;
}

export interface StudyMindMapAiCrossLink {
  from: string;
  to: string;
  label?: string;
  style?: StudyMindMapAiLinkStyle;
}

/** A new map or a new branch, without coordinates; the app lays it out (§5.4). */
export interface StudyMindMapAiTree {
  kind: 'tree';
  title: string;
  centre: StudyMindMapAiIdea;
  cross_links: StudyMindMapAiCrossLink[];
}

/** One change of an `Ask AI` result (§5.6). Ids are map ids or `temp_id`s of earlier ops. */
export type StudyMindMapChangeOp = { op_id: string } & (
  | {
      op: 'add_idea';
      parent_id: string;
      temp_id: string;
      text: string;
      kind?: StudyMindMapAiKind;
      note?: string;
      link_label?: string;
      link_style?: StudyMindMapAiLinkStyle;
    }
  | { op: 'update_idea'; id: string; text?: string; kind?: StudyMindMapAiKind; note?: string }
  | { op: 'add_link'; from: string; to: string; label?: string; style?: StudyMindMapAiLinkStyle }
  | { op: 'style_link'; id: string; label?: string; style: StudyMindMapAiLinkStyle }
  | { op: 'remove_idea'; id: string }
  | { op: 'remove_link'; id: string }
  | { op: 'frame'; temp_id: string; text: string; member_ids: string[] }
);

export interface StudyMindMapAiChanges {
  kind: 'changes';
  ops: StudyMindMapChangeOp[];
}

export type StudyMindMapDraftResult = StudyMindMapAiTree | StudyMindMapAiChanges;

export interface StudyMindMapDraftQuote {
  stars: number;
  analysis_count: number;
  max_nodes: number;
  model: string;
}

export interface StudyMindMapDraftNode {
  temp_id: string;
  text: string;
  source: Extract<StudyMindMapSource, { type: 'analysis_item' }>;
}

export interface StudyMindMapDraft {
  id: string;
  status: 'generating' | 'ready' | 'failed';
  analysis_ids: string[];
  document_ids: string[];
  title: string | null;
  nodes: StudyMindMapDraftNode[];
  edges: Array<{ from: string; to: string; label?: string }>;
  /** Absent on receipts made before the mind map skill (§5.4): those use `nodes` / `edges`. */
  mode?: StudyMindMapDraftMode;
  prompt_version?: string | null;
  result?: StudyMindMapDraftResult | null;
  stars_charged: number;
  stars_refunded: number;
  refunded: boolean;
  error_code: string | null;
  applied: boolean;
  map_id: string | null;
  created_at: string;
  completed_at: string | null;
}

export const STUDY_MIND_MAPS_QUERY_KEY = (subjectKey: string) =>
  ['study-mind-maps', subjectKey] as const;
export const STUDY_MIND_MAP_QUERY_KEY = (mapId: string) => ['study-mind-map', mapId] as const;
export const STUDY_MIND_MAP_REVISIONS_QUERY_KEY = (mapId: string) =>
  ['study-mind-map-revisions', mapId] as const;

/** The editor route (PRD §4). */
export function mindMapPath(subjectKey: string, mapId: string, search = '') {
  return `/learn/study/subjects/${encodeURIComponent(subjectKey)}/mind-maps/${encodeURIComponent(mapId)}${search}`;
}

/** The Mind maps tab of a subject (PRD §3). */
export function mindMapsTabPath(subjectKey: string) {
  return `/learn/study/subjects/${encodeURIComponent(subjectKey)}?tab=mind-maps`;
}

export const listStudyMindMaps = (subjectKey: string) =>
  api<StudyMindMapList>(`/study/mind-maps?subject_key=${encodeURIComponent(subjectKey)}`);

export const getStudyMindMap = (mapId: string) =>
  api<StudyMindMap>(`/study/mind-maps/${encodeURIComponent(mapId)}`);

export const createStudyMindMap = (input: {
  subject_key: string;
  title?: string;
  term_id?: string;
  content?: StudyMindMapContent;
  draft_id?: string;
}) => api<StudyMindMap>('/study/mind-maps', { method: 'POST', body: input });

export interface SaveStudyMindMapInput {
  expected_version: number;
  title?: string;
  content: StudyMindMapContent;
  draft_id?: string;
}

export const saveStudyMindMap = (mapId: string, input: SaveStudyMindMapInput) =>
  api<StudyMindMap>(`/study/mind-maps/${encodeURIComponent(mapId)}`, {
    method: 'PUT',
    body: input,
  });

export const updateStudyMindMap = (
  mapId: string,
  input: { title?: string; subject_key?: string; expected_version?: number },
) =>
  api<StudyMindMap>(`/study/mind-maps/${encodeURIComponent(mapId)}`, {
    method: 'PATCH',
    body: input,
  });

export const deleteStudyMindMap = (mapId: string) =>
  api<{ id: string; deleted: true }>(`/study/mind-maps/${encodeURIComponent(mapId)}`, {
    method: 'DELETE',
  });

export const duplicateStudyMindMap = (mapId: string, title?: string) =>
  api<StudyMindMap>(`/study/mind-maps/${encodeURIComponent(mapId)}/duplicate`, {
    method: 'POST',
    body: title ? { title } : {},
  });

export const listStudyMindMapRevisions = (mapId: string) =>
  api<StudyMindMapRevisions>(`/study/mind-maps/${encodeURIComponent(mapId)}/revisions`);

export const getStudyMindMapRevision = (mapId: string, version: number) =>
  api<StudyMindMapRevision>(`/study/mind-maps/${encodeURIComponent(mapId)}/revisions/${version}`);

export const restoreStudyMindMapRevision = (
  mapId: string,
  version: number,
  expectedVersion: number,
) =>
  api<StudyMindMap>(`/study/mind-maps/${encodeURIComponent(mapId)}/revisions/${version}/restore`, {
    method: 'POST',
    body: { expected_version: expectedVersion },
  });

/** What one AI call is about. The quote takes the same fields and never charges. */
export interface StudyMindMapDraftRequest {
  mode: StudyMindMapDraftMode;
  subject_key: string;
  /** `materials` and `add`: 1–5 finished analyses. */
  analysis_ids?: string[];
  /** `topic`: 1–120 characters. */
  topic?: string;
  /** `add` and `change`: the open map. */
  map_id?: string;
  /** `change`: the saved version the AI reads. */
  base_version?: number;
  /** `change`: what the learner wants, 1–300 characters. */
  instruction?: string;
  /** `change`: changes stay under this idea. */
  scope_node_id?: string;
}

export const STUDY_MIND_MAP_TOPIC_MAX = 120;
export const STUDY_MIND_MAP_INSTRUCTION_MAX = 300;

export const quoteStudyMindMapDraft = (
  input: Pick<
    StudyMindMapDraftRequest,
    'mode' | 'subject_key' | 'analysis_ids' | 'topic' | 'map_id' | 'instruction'
  >,
) =>
  api<StudyMindMapDraftQuote>('/study/mind-maps/drafts/quote', {
    method: 'POST',
    body: input,
  });

/** One paid AI call. Runs on the server for up to about 90 seconds. */
export const createStudyMindMapDraft = (
  input: StudyMindMapDraftRequest & { expected_stars: number; idempotency_key: string },
) => api<StudyMindMapDraft>('/study/mind-maps/drafts', { method: 'POST', body: input });

export const getStudyMindMapDraft = (draftId: string) =>
  api<StudyMindMapDraft>(`/study/mind-maps/drafts/${encodeURIComponent(draftId)}`);

/** A fresh idempotency key for one paid draft attempt. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID();
  // RFC 4122 v4 shape for very old browsers; the server only needs a unique uuid.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
}

interface ErrorShape {
  status?: number;
  code?: string;
  message?: string;
  details?: unknown;
}

function shapeOf(error: unknown): ErrorShape | null {
  if (!error || typeof error !== 'object') return null;
  const value = error as ErrorShape;
  return typeof value.code === 'string' ? value : null;
}

/** The error code of a failed call, or null. */
export function mindMapErrorCode(error: unknown): string | null {
  return shapeOf(error)?.code ?? null;
}

/** True when another device saved this map first (PRD §4.4). */
export function isVersionConflict(error: unknown): boolean {
  return mindMapErrorCode(error) === 'STUDY_MIND_MAP_VERSION_CHANGED';
}

/** True when a save can be tried again by itself: a network error or a server fault. */
export function isRetryableSaveError(error: unknown): boolean {
  const shape = shapeOf(error);
  if (!shape) return true;
  return typeof shape.status !== 'number' || shape.status >= 500 || shape.status === 429;
}

function tooLargeMessage(error: ErrorShape): string {
  const details =
    error.details && typeof error.details === 'object'
      ? (error.details as Record<string, unknown>)
      : {};
  const max = typeof details.max === 'number' ? details.max : null;
  if (details.limit === 'notes')
    return `This map's notes are too long to save. A map can hold up to ${(max ?? STUDY_MIND_MAP_LIMITS.notes_total_max).toLocaleString('en-AU')} characters of notes. Shorten some notes and it saves again.`;
  if (details.limit === 'edges')
    return `A map can have up to ${max ?? STUDY_MIND_MAP_LIMITS.edges_per_map} links. Remove a link to add more.`;
  return `A map can have up to ${max ?? STUDY_MIND_MAP_LIMITS.nodes_per_map} ideas. Remove an idea or start a new map.`;
}

/** Learner copy for a failed mind map or draft call. It never shows an internal code. */
export function mindMapErrorMessage(error: unknown, fallback: string): string {
  const shape = shapeOf(error);
  if (!shape) return fallback;
  const access = studyAccessMessage(error);
  if (access) return access;
  switch (shape.code) {
    case 'STUDY_MIND_MAP_LIMIT':
      return STUDY_MIND_MAP_LIMIT_MESSAGE;
    case 'STUDY_MIND_MAP_TOO_LARGE':
      return tooLargeMessage(shape);
    case 'STUDY_MIND_MAP_VERSION_CHANGED':
      return STUDY_MIND_MAP_CONFLICT_MESSAGE;
    case 'STUDY_MIND_MAP_NOT_FOUND':
      return 'This map is no longer here. It may have been deleted.';
    case 'STUDY_MIND_MAP_CONTENT_INVALID':
      return "This map can't be opened right now. Try again later.";
    case 'STUDY_MIND_MAP_REVISION_NOT_FOUND':
      return 'This version is no longer kept.';
    case 'STUDY_SUBJECT_NOT_FOUND':
      return 'This subject is not in your Study subjects.';
    case 'STUDY_TERM_NOT_FOUND':
      return 'This Term is not in your Study year.';
    case 'STUDY_MIND_MAP_DRAFT_NOT_FOUND':
      return 'This draft is no longer here. Make a new draft.';
    case 'STUDY_MIND_MAP_DRAFT_NOT_READY':
      return 'This draft is not ready yet.';
    case 'STUDY_MIND_MAP_DRAFT_USED':
      return 'This draft was already added to a map.';
    case 'STUDY_MIND_MAP_DRAFT_INSERT_INVALID':
      return "These ideas can't be added to this map. Load the map again and try once more.";
    case 'STUDY_MIND_MAP_CHANGES_NOT_APPLICABLE':
      return 'None of the ticked changes fit this map now. Discard them and ask again.';
    case 'STUDY_MIND_MAP_NOT_SAVED':
      return 'Your map is not saved yet. Wait until it says Saved, then try again.';
    case 'STUDY_MIND_MAP_DRAFT_IDEMPOTENCY_CONFLICT':
      return 'Your choice of materials changed. Check it and try again.';
    case 'STUDY_ANALYSIS_NOT_AVAILABLE':
      return shape.message === 'Mind map drafts are not available on this computer yet.'
        ? shape.message
        : ANALYSIS_NOT_FOR_SCHOOL;
    case 'STUDY_ANALYSIS_PAUSED':
    case 'STUDY_ANALYSIS_ROUTE_UNAVAILABLE':
      return 'AI drafts are paused right now. You can still make and edit maps yourself.';
    case 'STUDY_ANALYSIS_NOT_READY':
      return 'Wait until the analysis of this material finishes, then try again.';
    default:
      return analysisErrorMessage(error, fallback);
  }
}

/** True when AI drafts cannot be used right now, so the draft buttons are turned off. */
export function draftsUnavailable(error: unknown): boolean {
  const code = mindMapErrorCode(error);
  return (
    code === 'STUDY_ANALYSIS_NOT_AVAILABLE' ||
    code === 'STUDY_ANALYSIS_PAUSED' ||
    code === 'STUDY_ANALYSIS_ROUTE_UNAVAILABLE' ||
    code === 'STUDY_PAID_PRICE_INVALID' ||
    studyAccessMessage(error) !== null
  );
}

/** The sentence after a failed draft. The server refunds every failed draft in full. */
export function draftFailureMessage(
  draft: Pick<StudyMindMapDraft, 'refunded' | 'error_code'> &
    Partial<Pick<StudyMindMapDraft, 'mode'>>,
) {
  const unsafe =
    draft.error_code === 'LLM_OUTPUT_REJECTED' || draft.error_code === 'MODERATION_UNAVAILABLE';
  const fromPages = !draft.mode || draft.mode === 'materials' || draft.mode === 'add';
  const base = unsafe
    ? fromPages
      ? "We couldn't make a safe draft from these pages."
      : "We couldn't make a safe result this time."
    : draft.mode === 'change'
      ? "AI couldn't change this map this time."
      : "The draft didn't work this time.";
  return draft.refunded ? `${base} Your Stars are back.` : `${base} We're returning your Stars.`;
}
